import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { FormikProps } from "formik";
import Grid2 from "@mui/material/Grid2"; // استفاده از Grid2
import SelectCity from "../../../components/SelectCity";
import { TypeUser } from "src/interface/enum/TypeUser";
import { useQuery, useMutation } from "@tanstack/react-query";
import { API_URL, handleChange } from "src/utilities";
import { BaseDefinitionType } from "src/interface/enum/BaseDefinitionType ";
import { useAuth } from "src/context/auth";
import { IBaseDefinition } from "src/interface/IBaseDefinition";

import { IAppUser } from "src/interface/IUser";
import {
  IAutoCompleteLabelValue,
  UserType,
} from "src/interface/enum/GeneralType";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { getRoutes } from "src/utilities/routes";
import { useNotifications } from "@toolpad/core/useNotifications";

const UserForm = ({
  formik,
  usersData,
  isEdit,
  tags,
}: {
  formik: FormikProps<any>;
  isEdit: boolean;
  usersData: IAppUser[];
  tags?: IAutoCompleteLabelValue[];
}) => {
  const { user } = useAuth();
  const notifications = useNotifications();
  const Routes = getRoutes();

  // Mutation برای بررسی یکتا بودن شماره پرونده
  const { mutateAsync: checkCaseNumberUnique } = useMutation({
    mutationFn: async ({
      caseNumber,
      excludeUserId,
    }: {
      caseNumber: string;
      excludeUserId?: string;
    }) => {
      const url = excludeUserId
        ? `${Routes.UsersRoutes}checkCaseNumber/${caseNumber}?excludeUserId=${excludeUserId}`
        : `${Routes.UsersRoutes}checkCaseNumber/${caseNumber}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      });
      return await response.json();
    },
  });

  // تابع بررسی یکتا بودن در زمان تایپ
  const handleCaseNumberChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    formik.setFieldValue("caseNumber", value);

    // بررسی یکتا بودن بعد از 500ms تاخیر
    if (value && value.length >= 3) {
      setTimeout(async () => {
        try {
          const checkResult = await checkCaseNumberUnique({
            caseNumber: value,
            excludeUserId: isEdit ? formik.values.id : undefined,
          });

          if (!checkResult.data) {
            formik.setFieldError("caseNumber", "شماره پرونده تکراری است");
          } else {
            // پاک کردن خطا در صورت یکتا بودن
            if (formik.errors.caseNumber === "شماره پرونده تکراری است") {
              formik.setFieldError("caseNumber", "");
            }
          }
        } catch (error) {
          console.error("خطا در بررسی یکتا بودن شماره پرونده:", error);
        }
      }, 500);
    }
  };

  const { data: MeetingData } = useQuery({
    queryKey: ["MeetingData"],
    queryFn: () =>
      fetch(
        API_URL + "base-definition/type/" + BaseDefinitionType.MeetingMethod,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Aceept: "application/json",
          },
        }
      ).then((res) => res.json()),
  });
  const [getMeeting, setMeeting] = React.useState<IAutoCompleteLabelValue[]>(
    []
  );
  React.useEffect(() => {
    if (MeetingData) {
      setMeeting(
        MeetingData.map((item: IBaseDefinition) => ({
          label: item.caption,
          value: item.id,
        }))
      );
    }
  }, [MeetingData]);

  // اگر tags از props پاس نشده باشد، از API دریافت کن
  const { data: TagsData } = useQuery({
    queryKey: ["TagsData"],
    queryFn: () =>
      fetch(Routes.BaseDefinitionWitTypehRoutes + BaseDefinitionType.Tags, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      }).then((res) => res.json()),
    enabled: !tags, // فقط اگر tags از props پاس نشده باشد
  });
  const [getTags, setTags] = React.useState<IAutoCompleteLabelValue[]>([]);
  React.useEffect(() => {
    if (tags) {
      // اگر tags از props پاس شده باشد، از آن استفاده کن
      setTags(tags);
    } else if (TagsData) {
      // در غیر این صورت از API دریافت کن
      const formattedTags = TagsData.map((item: IBaseDefinition) => ({
        label: item.caption,
        value: item.id,
        item: item,
      }));
      setTags(formattedTags);
    }
  }, [TagsData, tags]);

  const { data: BaseActivityData } = useQuery({
    queryKey: ["BaseActivityData"],
    queryFn: () =>
      fetch(
        API_URL + "base-definition/type/" + BaseDefinitionType.BaseActivity,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Aceept: "application/json",
          },
        }
      ).then((res) => res.json()),
  });
  const [getBaseActivity, setBaseActivity] = React.useState<
    IAutoCompleteLabelValue[]
  >([]);
  React.useEffect(() => {
    if (BaseActivityData) {
      setBaseActivity(
        BaseActivityData.map((item: IBaseDefinition) => ({
          label: item.caption,
          value: item.id,
        }))
      );
    }
  }, [BaseActivityData]);

  const [getUsers, setUsers] = React.useState<IAutoCompleteLabelValue[]>([]);
  React.useEffect(() => {
    if (usersData.length > 0) {
      setUsers(
        usersData.map((item: IAppUser) => ({
          label: item.fName + " " + item.lName,
          value: item.id,
        }))
      );
    }
  }, [usersData]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid2 container spacing={2} marginY={2} columns={15}>
        <Grid2 size={3}>
          <Stack direction={"row"} alignItems={"center"}>
            <Typography width={85}> نوع کاربر: </Typography>
            <ToggleButtonGroup
              fullWidth
              size="small"
              color="primary"
              value={formik.values.roleName}
              exclusive
              onChange={(event: any, newValue: any) =>
                handleChange(event, newValue, "roleName", formik)
              }
              aria-label="Platform"
            >
              <ToggleButton value="Customer">مشتری</ToggleButton>
              <ToggleButton value="Editor">کاربر سامانه</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Grid2>
        {formik.values.roleName === TypeUser.CUSTOMER && (
          <Grid2 size={3}>
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <Typography> ارسال پیامک: </Typography>
              <ToggleButtonGroup
                size="small"
                color="primary"
                value={formik.values.isSendBalanceSms}
                exclusive
                onChange={(event: any, newValue: any) =>
                  handleChange(event, newValue, "isSendBalanceSms", formik)
                }
                aria-label="Platform"
              >
                <ToggleButton value="true">بله</ToggleButton>
                <ToggleButton value="false">خیر</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid2>
        )}
        {formik.values.roleName !== TypeUser.CUSTOMER && (
          <>
            {(formik.values.roleName === TypeUser.EDITOR ||
              formik.values.roleName === TypeUser.ADMIN) && (
              <Grid2 size={3}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography> وضعیت: </Typography>
                  <ToggleButtonGroup
                    size="small"
                    color="primary"
                    value={formik.values.isActive || "true"}
                    exclusive
                    onChange={(event: any, newValue: any) =>
                      handleChange(event, newValue, "isActive", formik)
                    }
                    aria-label="Platform"
                  >
                    <ToggleButton value="true">فعال</ToggleButton>
                    <ToggleButton value="false">غیرفعال</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Grid2>
            )}
            <Grid2 size={4}>
              <TextField
                size="small"
                {...formik.getFieldProps("userName")} // استفاده از ویژگی‌های فرمیک
                label="نام کاربری"
                placeholder="نام کاربری"
                error={
                  formik.touched.userName && Boolean(formik.errors.userName)
                }
                helperText={
                  (formik.touched.userName && formik.errors.userName) as boolean
                }
                variant="outlined"
                required={formik.values.roleName !== TypeUser.CUSTOMER}
                fullWidth
              />
            </Grid2>
            <Grid2 size={4}>
              {(!isEdit && (
                <TextField
                  size="small"
                  {...formik.getFieldProps("password")} // استفاده از ویژگی‌های فرمیک
                  label="گذرواژه"
                  required={formik.values.roleName !== TypeUser.CUSTOMER}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={
                    (formik.touched.password &&
                      formik.errors.password) as boolean
                  }
                  variant="outlined"
                  fullWidth
                />
              )) || (
                <TextField
                  size="small"
                  {...formik.getFieldProps("newPassword")} // استفاده از ویژگی‌های فرمیک
                  label="گذرواژه جدید"
                  required={formik.values.roleName !== TypeUser.CUSTOMER}
                  error={
                    formik.touched.newPassword &&
                    Boolean(formik.errors.newPassword)
                  }
                  helperText={
                    (formik.touched.newPassword &&
                      formik.errors.newPassword) as boolean
                  }
                  variant="outlined"
                  fullWidth
                />
              )}
            </Grid2>
            <Grid2 size={4}>
              <TextField
                size="small"
                {...formik.getFieldProps("confirmedPassword")} // استفاده از ویژگی‌های فرمیک
                label="تکرار گذرواژه"
                error={
                  formik.touched.confirmedPassword &&
                  Boolean(formik.errors.confirmedPassword)
                }
                helperText={
                  (formik.touched.confirmedPassword &&
                    formik.errors.confirmedPassword) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
          </>
        )}
      </Grid2>

      {formik.values.roleName === TypeUser.CUSTOMER && (
        <>
          <Grid2 container spacing={2} marginY={2} columns={13}>
            <Grid2 size={4}>
              <Stack position={"relative"}>
                <Autocomplete
                  multiple
                  getOptionLabel={(option) => option.label}
                  renderTags={(values, getTagProps) => {
                    return values.map((tag, index) => (
                      <Chip
                        label={tag.label}
                        {...getTagProps({ index })}
                        sx={{
                          backgroundColor: tag?.item?.other,
                          color: "white",
                        }}
                      />
                    ));
                  }}
                  //@ts-ignore
                  value={formik.values.tagRef || []}
                  // value={formik.values.tagRef || []}
                  onChange={(event: any, newValue: any) => {
                    // console.log("🚀 ~ newValue:", newValue);
                    formik.setFieldValue("tagRef", newValue || undefined);
                  }}
                  options={getTags}
                  fullWidth
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      error={
                        formik.touched.tagRef && Boolean(formik.errors.tagRef)
                      }
                      helperText={
                        (formik.touched.tagRef &&
                          formik.errors.tagRef) as boolean
                      }
                      {...params}
                      label="تگ"
                      placeholder="تگ"
                    />
                  )}
                />
                <div
                  className="top-0 bottom-0 right-0 absolute w-2 rounded-r-md "
                  style={{
                    backgroundColor: getTags.find(
                      //@ts-ignore
                      (t) => t.value === formik.values.tagRef
                      //@ts-ignore
                    )?.item?.other,
                  }}
                />
              </Stack>
            </Grid2>
            <Grid2 size={3}>
              <Autocomplete
                disablePortal
                value={
                  getMeeting.find(
                    (t) => t.value === formik.values.meetingMethodRef
                  ) || null
                }
                onChange={(event: any, newValue: any) => {
                  formik.setFieldValue(
                    "meetingMethodRef",
                    newValue?.value || undefined
                  );
                }}
                options={getMeeting || []}
                fullWidth
                size="small"
                renderInput={(params) => (
                  <TextField
                    error={
                      formik.touched.meetingMethodRef &&
                      Boolean(formik.errors.meetingMethodRef)
                    }
                    helperText={
                      (formik.touched.meetingMethodRef &&
                        formik.errors.meetingMethodRef) as boolean
                    }
                    {...params}
                    label="نحوه آشنایی"
                    placeholder="نحوه آشنایی"
                  />
                )}
              />
            </Grid2>
            <Grid2 size={3}>
              <Autocomplete
                disablePortal
                value={
                  getUsers.find(
                    (item) => item.value === formik.values.referrerRef
                  ) || null
                }
                onChange={(event: any, newValue: any) => {
                  formik.setFieldValue(
                    "referrerRef",
                    newValue?.value || undefined
                  );
                }}
                options={getUsers || []}
                fullWidth
                size="small"
                renderInput={(params) => (
                  <TextField
                    error={
                      formik.touched.referrerRef &&
                      Boolean(formik.errors.referrerRef)
                    }
                    helperText={
                      (formik.touched.referrerRef &&
                        formik.errors.referrerRef) as boolean
                    }
                    {...params}
                    label="معرف"
                    placeholder="معرف"
                  />
                )}
              />
            </Grid2>
            <Grid2 size={3}>
              <TextField
                size="small"
                name="caseNumber"
                value={formik.values.caseNumber}
                onChange={handleCaseNumberChange}
                onBlur={formik.handleBlur}
                label="شماره پرونده تکنو"
                placeholder="شماره پرونده تکنو"
                error={
                  formik.touched.caseNumber && Boolean(formik.errors.caseNumber)
                }
                helperText={
                  (formik.touched.caseNumber &&
                    formik.errors.caseNumber) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
          </Grid2>
        </>
      )}
      <Divider textAlign="left">
        اطلاعات{" "}
        {formik.values.roleName === TypeUser.CUSTOMER ? "مدیر/مالک" : "شخصی"}
      </Divider>

      <Grid2 container spacing={2} marginY={2}>
        <Grid2 size={2}>
          <Stack spacing={2} alignItems={"center"} direction={"row"}>
            <Typography> جنسیت*: </Typography>
            <ToggleButtonGroup
              size="small"
              color="primary"
              fullWidth
              value={formik.values.gender}
              exclusive
              onChange={(event: any, newValue: any) =>
                handleChange(event, newValue, "gender", formik)
              }
              aria-label="Platform"
            >
              <ToggleButton value="false">مرد</ToggleButton>
              <ToggleButton value="true">زن</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Grid2>
        <Grid2 size={3}>
          <TextField
            size="small"
            {...formik.getFieldProps("fName")} // استفاده از ویژگی‌های فرمیک
            label="نام*"
            error={formik.touched.fName && Boolean(formik.errors.fName)}
            helperText={
              (formik.touched.fName && formik.errors.fName) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={3}>
          <TextField
            size="small"
            {...formik.getFieldProps("lName")} // استفاده از ویژگی‌های فرمیک
            label="نام خانوادگی*"
            error={formik.touched.lName && Boolean(formik.errors.lName)}
            helperText={
              (formik.touched.lName && formik.errors.lName) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={2}>
          <TextField
            size="small"
            {...formik.getFieldProps("phoneNumber")} // استفاده از ویژگی‌های فرمیک
            label="موبایل*"
            error={
              formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
            }
            helperText={
              (formik.touched.phoneNumber &&
                formik.errors.phoneNumber) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={2}>
          <TextField
            size="small"
            {...formik.getFieldProps("nationalCode")} // استفاده از ویژگی‌های فرمیک
            label="کد ملی"
            placeholder="کد ملی*"
            error={
              formik.touched.nationalCode && Boolean(formik.errors.nationalCode)
            }
            helperText={
              (formik.touched.nationalCode &&
                formik.errors.nationalCode) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
      </Grid2>
      <Grid2 container spacing={2} marginY={2}>
        <Grid2 size={2}>
          <DatePicker
            slotProps={{
              textField: {
                size: "small",
              },
            }}
            label="تاریخ تولد"
            onChange={(date: Date | null) =>
              formik.setFieldValue("birthDate", date)
            }
            value={
              (formik.values.birthDate && new Date(formik.values.birthDate)) ||
              null
            }
            //defaultValue={new Date()}
          />
        </Grid2>
        <Grid2 size={3}>
          <TextField
            size="small"
            {...formik.getFieldProps("telNumber")} // استفاده از ویژگی‌های فرمیک
            label="تلفن ثابت"
            placeholder="تلفن ثابت"
            error={formik.touched.telNumber && Boolean(formik.errors.telNumber)}
            helperText={
              (formik.touched.telNumber && formik.errors.telNumber) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={3}>
          <TextField
            size="small"
            {...formik.getFieldProps("nationalIDBack")} // استفاده از ویژگی‌های فرمیک
            label="سریال پشت کارت ملی"
            placeholder="سریال پشت کارت ملی"
            error={
              formik.touched.nationalIDBack &&
              Boolean(formik.errors.nationalIDBack)
            }
            helperText={
              (formik.touched.nationalIDBack &&
                formik.errors.nationalIDBack) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={2}>
          <TextField
            size="small"
            {...formik.getFieldProps("fatherName")} // استفاده از ویژگی‌های فرمیک
            label="نام پدر"
            placeholder="نام پدر"
            error={
              formik.touched.fatherName && Boolean(formik.errors.fatherName)
            }
            helperText={
              (formik.touched.fatherName && formik.errors.fatherName) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={2}>
          <TextField
            size="small"
            {...formik.getFieldProps("email")} // استفاده از ویژگی‌های فرمیک
            label="ایمیل"
            placeholder="ایمیل"
            type="email"
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={
              (formik.touched.email && formik.errors.email) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
      </Grid2>
      {formik.values.roleName === TypeUser.CUSTOMER && (
        <>
          <Divider textAlign="left">اطلاعات تجاری</Divider>
          <Grid2 container spacing={2} marginY={2} columns={16}>
            <Grid2 size={4}>
              <Stack spacing={2} alignItems={"center"} direction={"row"}>
                <Typography width={135}> نوع مشتری: </Typography>
                <ToggleButtonGroup
                  size="small"
                  color="primary"
                  value={formik.values.typeUser}
                  exclusive
                  fullWidth
                  onChange={(event: any, newValue: any) =>
                    handleChange(event, newValue, "typeUser", formik)
                  }
                  aria-label="Platform"
                >
                  <ToggleButton value="true">حقیقی</ToggleButton>
                  <ToggleButton value="false">حقوقی</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Grid2>
            {formik.values.typeUser !== "false" && (
              <Grid2 size={4}>
                <Stack spacing={2} alignItems={"center"} direction={"row"}>
                  <Typography width={135}> نوع حقیقی: </Typography>
                  <ToggleButtonGroup
                    size="small"
                    color="primary"
                    value={formik.values.coType}
                    exclusive
                    onChange={(event: any, newValue: any) =>
                      handleChange(event, newValue, "coType", formik)
                    }
                    fullWidth
                    aria-label="Platform"
                  >
                    <ToggleButton value="true">مشارکتی</ToggleButton>
                    <ToggleButton value="false">انفرادی</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Grid2>
            )}
            <Grid2 size={formik.values.typeUser !== "false" ? 3 : 7}>
              <TextField
                size="small"
                {...formik.getFieldProps("coName")} // استفاده از ویژگی‌های فرمیک
                label="نام تجاری"
                placeholder="نام تجاری"
                error={formik.touched.coName && Boolean(formik.errors.coName)}
                helperText={
                  (formik.touched.coName && formik.errors.coName) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
            {formik.values.typeUser === UserType.Hoghoghi && (
              <Grid2 size={2}>
                <TextField
                  size="small"
                  {...formik.getFieldProps("nationalId")} // استفاده از ویژگی‌های فرمیک
                  label="شناسه ملی"
                  placeholder="شناسه ملی"
                  error={
                    formik.touched.nationalId &&
                    Boolean(formik.errors.nationalId)
                  }
                  helperText={
                    (formik.touched.nationalId &&
                      formik.errors.nationalId) as boolean
                  }
                  variant="outlined"
                  fullWidth
                />
              </Grid2>
            )}
            <Grid2 size={formik.values.typeUser === UserType.Hoghoghi ? 3 : 5}>
              <TextField
                size="small"
                {...formik.getFieldProps("economicCode")} // استفاده از ویژگی‌های فرمیک
                label="کد اقتصادی جدید"
                placeholder="کد اقتصادی جدید"
                disabled={formik.values.typeUser === UserType.Hoghoghi}
                error={
                  formik.touched.economicCode &&
                  Boolean(formik.errors.economicCode)
                }
                value={
                  formik.values.typeUser === UserType.Hoghoghi
                    ? formik.values.nationalId || ""
                    : formik.values.economicCode || ""
                }
                helperText={
                  (formik.touched.economicCode &&
                    formik.errors.economicCode) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
          </Grid2>
          <Grid2 container spacing={2} marginY={2}>
            <SelectCity
              size="small"
              formik={formik}
              citySize={3}
              provinceSize={3}
            />
            <Grid2 size={3}>
              <TextField
                size="small"
                {...formik.getFieldProps("postalCode")} // استفاده از ویژگی‌های فرمیک
                label="کد پستی"
                placeholder="کد پستی"
                error={
                  formik.touched.postalCode && Boolean(formik.errors.postalCode)
                }
                helperText={
                  (formik.touched.postalCode &&
                    formik.errors.postalCode) as boolean
                }
                type="number"
                variant="outlined"
                fullWidth
              />
            </Grid2>

            <Grid2 size={3}>
              <TextField
                size="small"
                {...formik.getFieldProps("coTaxFileClass")} // استفاده از ویژگی‌های فرمیک
                label="کلاسه پرونده مالیاتی"
                error={
                  formik.touched.coTaxFileClass &&
                  Boolean(formik.errors.coTaxFileClass)
                }
                helperText={
                  (formik.touched.coTaxFileClass &&
                    formik.errors.coTaxFileClass) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
          </Grid2>
          <Grid2 container spacing={2} marginY={2} columns={14}>
            <Grid2 size={3}>
              <TextField
                size="small"
                {...formik.getFieldProps("taxAreaCode")} // استفاده از ویژگی‌های فرمیک
                label="کد حوزه مالیاتی"
                error={
                  formik.touched.taxAreaCode &&
                  Boolean(formik.errors.taxAreaCode)
                }
                helperText={
                  (formik.touched.taxAreaCode &&
                    formik.errors.taxAreaCode) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
            <Grid2 size={3}>
              <Autocomplete
                disablePortal
                value={
                  getBaseActivity.find(
                    (t) => t.value === formik.values.baseActivityRef
                  ) || null
                }
                onChange={(event: any, newValue: any) => {
                  formik.setFieldValue(
                    "baseActivityRef",
                    newValue?.value || undefined
                  );
                }}
                size="small"
                options={getBaseActivity || []}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    error={
                      formik.touched.baseActivityRef &&
                      Boolean(formik.errors.baseActivityRef)
                    }
                    helperText={
                      (formik.touched.baseActivityRef &&
                        formik.errors.baseActivityRef) as boolean
                    }
                    {...params}
                    label="زمینه فعالیت"
                    placeholder="زمینه فعالیت"
                  />
                )}
              />
            </Grid2>

            <Grid2 size={6}>
              <TextField
                size="small"
                {...formik.getFieldProps("address")} // استفاده از ویژگی‌های فرمیک
                label="آدرس"
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={
                  (formik.touched.address && formik.errors.address) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                size="small"
                {...formik.getFieldProps("coTelNumber")} // استفاده از ویژگی‌های فرمیک
                label="تلفن ثابت"
                error={
                  formik.touched.coTelNumber &&
                  Boolean(formik.errors.coTelNumber)
                }
                helperText={
                  (formik.touched.coTelNumber &&
                    formik.errors.coTelNumber) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
          </Grid2>
          <Grid2 container spacing={2} marginY={2}>
            <Grid2 size={6}>
              <TextField
                size="small"
                {...formik.getFieldProps("economicCode_OLD")} // استفاده از ویژگی‌های فرمیک
                label="کد اقتصادی قدیمی"
                placeholder="کد اقتصادی قدیمی"
                error={
                  formik.touched.economicCode_OLD &&
                  Boolean(formik.errors.economicCode_OLD)
                }
                helperText={
                  (formik.touched.economicCode_OLD &&
                    formik.errors.economicCode_OLD) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                size="small"
                {...formik.getFieldProps("folderPath")} // استفاده از ویژگی‌های فرمیک
                label="مسیر پوشه"
                placeholder="مسیر پوشه"
                error={
                  formik.touched.folderPath && Boolean(formik.errors.folderPath)
                }
                helperText={
                  (formik.touched.folderPath &&
                    formik.errors.folderPath) as boolean
                }
                variant="outlined"
                fullWidth
              />
            </Grid2>
          </Grid2>
        </>
      )}
    </Box>
  );
};

export default UserForm;
